import { Divider, Stack, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';
import { DailyOpeningCount } from 'v2/hooks/useWeeklyAccessEvents/useWeeklyAccessEvents';
import ChartCard, { CHART_HEIGHT } from 'v2/pages/Dashboard/charts/ChartCard';
import { formatDate } from 'v2/utils/dateTime';

const BAR_BORDER_RADIUS = 4;
// keeps bars thin instead of filling the whole band
const CATEGORY_GAP_RATIO = 0.6;
// just enough for the widest tick label; the 45px default leaves a visible gap on the left
const Y_AXIS_WIDTH = 28;
// The axis sizes already reserve room for the labels, so left/bottom need no extra margin.
// `top` is different: the charts' own `shortenLabels` gives the topmost y tick label a height
// budget equal to its distance from the top edge, and drops the label entirely when the text
// does not fit. A 12px caption needs ~20px, so anything less silently hides the highest value.
const CHART_MARGIN = { top: 24, right: 8, bottom: 0, left: 0 };

interface Props {
  dailyOpenings: DailyOpeningCount[];
  averageOpeningsPerDay: number;
  peakDay?: DailyOpeningCount;
  isLoading: boolean;
  hasError: boolean;
}

const DeviceOpeningsPerDay: React.FC<Props> = ({
  dailyOpenings,
  averageOpeningsPerDay,
  peakDay,
  isLoading,
  hasError
}) => {
  const { translation } = useTranslation();
  const theme = useTheme();

  const peakDayLabel = peakDay
    ? `${formatDate(peakDay.date, 'weekdayWithDate')} (${peakDay.count})`
    : EMPTY_VALUE_PLACEHOLDER;

  // every day in the window gets a bucket, quiet ones included, so an all-zero week has nothing to plot
  const hasOpenings = dailyOpenings.some(({ count }) => count > 0);

  return (
    <ChartCard
      title={translation.weekly_overview}
      subtitle={translation.device_openings_per_day}
      isLoading={isLoading}
      hasError={hasError}
      isEmpty={!hasOpenings}
      emptyStateContent={translation.dashboard_empty_weekly_openings_content}
    >
      <BarChart
        height={CHART_HEIGHT}
        margin={CHART_MARGIN}
        series={[
          {
            data: dailyOpenings.map(({ count }) => count),
            color: theme.palette.primary.main,
            label: translation.device_openings_per_day
          }
        ]}
        xAxis={[
          {
            scaleType: 'band',
            data: dailyOpenings.map(({ date }) => date),
            // the axis only has room for the narrow weekday; the tooltip spells out which date it is,
            // which matters because the window rolls and the same weekday moves position each day
            valueFormatter: (date: string, { location }) =>
              formatDate(date, location === 'tick' ? 'weekdayNarrow' : 'weekdayWithDate'),
            categoryGapRatio: CATEGORY_GAP_RATIO,
            disableTicks: true
          }
        ]}
        yAxis={[{ width: Y_AXIS_WIDTH, disableTicks: true }]}
        grid={{ horizontal: true, vertical: true }}
        borderRadius={BAR_BORDER_RADIUS}
        hideLegend
      />

      <Divider />

      <Stack gap={0.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {translation.weekly_average}
          </Typography>
          <Typography variant="subtitle2">{averageOpeningsPerDay}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {translation.peak_day}
          </Typography>
          <Typography variant="subtitle2">{peakDayLabel}</Typography>
        </Stack>
      </Stack>
    </ChartCard>
  );
};

export default DeviceOpeningsPerDay;
